import React from 'react';
import {
  Menu,
  Box,
  Typography,
  IconButton,
  Button,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip,
  alpha
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  PersonAdd as PersonAddIcon,
  School as SchoolIcon,
  Cancel as CancelIcon,
  LocalOffer as LocalOfferIcon,
  DirectionsCar as DirectionsCarIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  MarkEmailRead as MarkReadIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationsContext';

const NotificationsDropdown = ({ anchorEl, open, onClose }) => {
  const navigate = useNavigate();
  const {
    getRecentNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    getNotificationConfig,
    unreadCount
  } = useNotifications();

  // SafeLine color scheme
  const safeLineColors = {
    flameOrange: '#ff6b35',
    brightOrange: '#ff9500',
    deepOrange: '#e55100',
    orangeTint: '#fff4f0',
    pureWhite: '#ffffff',
    lightGray: '#f8f9fa',
    coolGray: '#e9ecef',
    mediumGray: '#dee2e6',
    emerald: '#10b981',
    lightEmerald: '#d1fae5',
    forestGreen: '#047857',
    amber: '#f59e0b',
    lightAmber: '#fef3c7',
    darkAmber: '#d97706',
    red: '#ef4444',
    lightRed: '#fee2e2',
    darkRed: '#dc2626',
    darkCharcoal: '#1f2937',
    mediumCharcoal: '#374151',
    lightCharcoal: '#6b7280',
    mutedGray: '#9ca3af'
  };

  const recentNotifications = getRecentNotifications(5);

  const getNotificationIcon = (type) => {
    const iconMap = {
      'person': <PersonIcon />,
      'security': <SecurityIcon />,
      'check_circle': <CheckCircleIcon />,
      'warning': <WarningIcon />,
      'person_add': <PersonAddIcon />,
      'school': <SchoolIcon />,
      'cancel': <CancelIcon />,
      'local_offer': <LocalOfferIcon />,
      'directions_car': <DirectionsCarIcon />,
      'info': <InfoIcon />
    };

    const config = getNotificationConfig(type);
    return iconMap[config.icon] || <InfoIcon />;
  };

  const getNotificationColor = (type) => {
    const config = getNotificationConfig(type);
    const colorMap = {
      'primary': safeLineColors.flameOrange,
      'warning': safeLineColors.amber,
      'success': safeLineColors.emerald,
      'error': safeLineColors.red,
      'info': safeLineColors.mediumCharcoal
    };
    return colorMap[config.color] || safeLineColors.mediumCharcoal;
  };

  const getNotificationBgColor = (type) => {
    const config = getNotificationConfig(type);
    const colorMap = {
      'primary': safeLineColors.orangeTint,
      'warning': safeLineColors.lightAmber,
      'success': safeLineColors.lightEmerald,
      'error': safeLineColors.lightRed,
      'info': safeLineColors.lightGray
    };
    return colorMap[config.color] || safeLineColors.lightGray;
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - new Date(timestamp);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const handleNotificationClick = (notification) => {
    const config = getNotificationConfig(notification.type);
    
    // Mark as read if not already read
    if (!notification.read) {
      markAsRead(notification.id);
    }

    // Navigate to the relevant page
    navigate(config.navigateTo);
    onClose();
  };

  const handleMarkAsRead = (notificationId, event) => {
    event.stopPropagation();
    markAsRead(notificationId);
  };

  const handleDeleteNotification = (notificationId, event) => {
    event.stopPropagation();
    deleteNotification(notificationId);
  };

  const handleSeeMore = () => {
    navigate('/notifications');
    onClose();
  };

  const handleMarkAllRead = () => {
    markAllAsRead();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      PaperProps={{
        elevation: 8,
        sx: {
          width: 420,
          maxHeight: 600,
          borderRadius: 3,
          border: `1px solid ${safeLineColors.coolGray}`,
          overflow: 'hidden',
          mt: 1,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`
          }
        }
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      {/* Header */}
      <Box sx={{ 
        p: 3, 
        backgroundColor: safeLineColors.orangeTint,
        borderBottom: `1px solid ${safeLineColors.coolGray}`
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 'bold',
            color: safeLineColors.darkCharcoal,
            display: 'flex',
            alignItems: 'center'
          }}>
            <NotificationsIcon sx={{ mr: 1, color: safeLineColors.flameOrange }} />
            Notifications
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {unreadCount > 0 && (
              <Tooltip title="Mark all as read">
                <IconButton 
                  size="small" 
                  onClick={handleMarkAllRead}
                  sx={{ 
                    color: safeLineColors.flameOrange,
                    '&:hover': {
                      backgroundColor: alpha(safeLineColors.flameOrange, 0.1)
                    }
                  }}
                >
                  <MarkReadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton 
              size="small" 
              onClick={onClose}
              sx={{ 
                color: safeLineColors.mediumCharcoal,
                '&:hover': {
                  backgroundColor: alpha(safeLineColors.mediumCharcoal, 0.1)
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ color: safeLineColors.mediumCharcoal }}>
            {unreadCount > 0 ? `${unreadCount} unread notifications` : 'All caught up!'}
          </Typography>
          {unreadCount > 0 && (
            <Chip 
              label={unreadCount}
              size="small"
              sx={{
                backgroundColor: safeLineColors.flameOrange,
                color: safeLineColors.pureWhite,
                fontWeight: 'bold',
                fontSize: '0.75rem'
              }}
            />
          )}
        </Box>
      </Box>

      {/* Notifications List */}
      <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
        {recentNotifications.length > 0 ? (
          <List sx={{ p: 0 }}>
            {recentNotifications.map((notification) => (
              <ListItem
                key={notification.id}
                button
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  py: 2,
                  px: 3,
                  borderBottom: `1px solid ${safeLineColors.coolGray}`,
                  backgroundColor: notification.read ? 'transparent' : alpha(safeLineColors.orangeTint, 0.3),
                  '&:hover': {
                    backgroundColor: alpha(safeLineColors.orangeTint, 0.5)
                  },
                  '&:last-child': {
                    borderBottom: 'none'
                  },
                  position: 'relative'
                }}
              >
                <ListItemIcon sx={{ minWidth: 48 }}>
                  <Avatar sx={{
                    backgroundColor: getNotificationBgColor(notification.type),
                    color: getNotificationColor(notification.type),
                    width: 40,
                    height: 40
                  }}>
                    {getNotificationIcon(notification.type)}
                  </Avatar>
                </ListItemIcon>
                
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          fontWeight: notification.read ? 'medium' : 'bold',
                          color: safeLineColors.darkCharcoal,
                          flex: 1
                        }}
                      >
                        {notification.title}
                      </Typography>
                      {!notification.read && (
                        <Box sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: safeLineColors.flameOrange
                        }} />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: safeLineColors.mediumCharcoal,
                          mb: 1,
                          lineHeight: 1.4
                        }}
                      >
                        {notification.message}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: safeLineColors.mutedGray,
                            fontWeight: 'medium'
                          }}
                        >
                          {formatTimeAgo(notification.timestamp)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {!notification.read && (
                            <Tooltip title="Mark as read">
                              <IconButton 
                                size="small"
                                onClick={(e) => handleMarkAsRead(notification.id, e)}
                                sx={{ 
                                  color: safeLineColors.flameOrange,
                                  '&:hover': {
                                    backgroundColor: alpha(safeLineColors.flameOrange, 0.1)
                                  }
                                }}
                              >
                                <MarkReadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Remove">
                            <IconButton 
                              size="small"
                              onClick={(e) => handleDeleteNotification(notification.id, e)}
                              sx={{ 
                                color: safeLineColors.mutedGray,
                                '&:hover': {
                                  color: safeLineColors.red,
                                  backgroundColor: alpha(safeLineColors.red, 0.1)
                                }
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Box sx={{ 
            p: 4, 
            textAlign: 'center',
            backgroundColor: safeLineColors.lightGray
          }}>
            <NotificationsIcon sx={{ 
              fontSize: 64, 
              color: safeLineColors.mutedGray,
              mb: 2
            }} />
            <Typography variant="h6" sx={{ 
              color: safeLineColors.mediumCharcoal,
              fontWeight: 'medium',
              mb: 1
            }}>
              No notifications
            </Typography>
            <Typography variant="body2" sx={{ 
              color: safeLineColors.mutedGray
            }}>
              You're all caught up! New notifications will appear here.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Footer Actions */}
      {recentNotifications.length > 0 && (
        <>
          <Divider />
          <Box sx={{ p: 2, backgroundColor: safeLineColors.orangeTint }}>
            <Button
              fullWidth
              onClick={handleSeeMore}
              startIcon={<ViewIcon />}
              sx={{
                borderRadius: 2,
                background: `linear-gradient(135deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`,
                color: safeLineColors.pureWhite,
                fontWeight: 'medium',
                textTransform: 'none',
                py: 1.5,
                '&:hover': {
                  background: `linear-gradient(135deg, ${safeLineColors.brightOrange}, ${safeLineColors.flameOrange})`
                }
              }}
            >
              View All Notifications
            </Button>
          </Box>
        </>
      )}
    </Menu>
  );
};

export default NotificationsDropdown; 